import { useEffect, useRef } from 'react';

export function RoomCodeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const r0 = useRef<HTMLInputElement>(null);
  const r1 = useRef<HTMLInputElement>(null);
  const r2 = useRef<HTMLInputElement>(null);
  const r3 = useRef<HTMLInputElement>(null);
  const refs = [r0, r1, r2, r3];
  const digits = [value[0] ?? '', value[1] ?? '', value[2] ?? '', value[3] ?? ''];

  useEffect(() => {
    if (!value) r0.current?.focus();
  }, []);

  const setAt = (i: number, d: string) => {
    const next = digits.slice();
    next[i] = d;
    onChange(next.join('').replace(/\s+/g, ''));
  };

  const onInput = (i: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    if (!raw) { setAt(i, ''); return; }
    if (raw.length >= 4) {
      onChange(raw.slice(0, 4));
      r3.current?.focus();
      return;
    }
    const ch = raw[raw.length - 1];
    if (!ch) return;
    setAt(i, ch);
    if (i < 3) refs[i + 1]?.current?.focus();
  };

  const onKey = (i: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      e.preventDefault();
      refs[i - 1]?.current?.focus();
      setAt(i - 1, '');
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs[i - 1]?.current?.focus();
    } else if (e.key === 'ArrowRight' && i < 3) {
      refs[i + 1]?.current?.focus();
    }
  };

  return (
    <div
      className="flex gap-2 justify-center"
      onPaste={(e) => {
        const txt = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 4);
        if (txt) {
          e.preventDefault();
          onChange(txt);
          const idx = Math.min(txt.length, 3);
          setTimeout(() => refs[idx]?.current?.focus(), 0);
        }
      }}
    >
      {[0, 1, 2, 3].map((i) => (
        <input
          key={i}
          ref={refs[i]}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digits[i] || ''}
          onChange={onInput(i)}
          onKeyDown={onKey(i)}
          onFocus={(e) => e.target.select()}
          className="w-14 h-16 text-center text-3xl font-black font-mono bg-slate-900/60 border-2 border-slate-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/30 rounded-2xl transition"
        />
      ))}
    </div>
  );
}
